import { useEffect, useState } from 'react'
import { remitosApi } from '../api/remitos'
import api from '../api/client'
import Boton from './Boton'
import Modal from './Modal'
import CampoLocalidad from './CampoLocalidad'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import { colors, text, space, sx, radius } from '../design/tokens'

/**
 * Envío parcial (Martin, 2 nov 2026): el remito salió entero en Presea y la
 * mercadería se despacha de a partes. Esta tarjeta muestra qué falta y abre
 * «Despachar parte». Lo pendiente lo calcula el backend
 * (EnvioParcialService → remito.envio_parcial_resumen); acá solo se pinta.
 */
export default function EnvioParcialCard({ remito, onCambio }) {
  const resumen = remito.envio_parcial_resumen
  const [abierto, setAbierto] = useState(false)
  if (!resumen) return null

  const puedeDespachar = remito.estado === 'confirmado' && resumen.unidades_pendientes > 0

  return (
    <div style={sx.card}>
      <div style={st.header}>
        <h3 style={{ ...sx.cardTitle, margin: 0 }}>📦 Envío parcial</h3>
        <div style={st.resumen}>
          {resumen.partes} {resumen.partes === 1 ? 'parte' : 'partes'} · {resumen.entregadas} {resumen.entregadas === 1 ? 'entregada' : 'entregadas'}
          {' · '}
          {resumen.unidades_pendientes > 0
            ? <strong style={{ color: colors.warn }}>faltan {resumen.unidades_pendientes} u.</strong>
            : <strong style={{ color: colors.ok }}>{resumen.completo ? 'completo' : 'todo despachado'}</strong>}
        </div>
        {puedeDespachar && (
          <Boton variante="primary" sm onClick={() => setAbierto(true)}>🚚 Despachar parte</Boton>
        )}
      </div>
      {remito.estado === 'ingresado' && (
        <p style={st.hint}>El remito tiene que estar confirmado para despachar partes.</p>
      )}
      <table style={sx.table}>
        <thead>
          <tr>
            <th style={sx.th}>Código</th>
            <th style={sx.th}>Descripción</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Total</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Despachado</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Pendiente</th>
          </tr>
        </thead>
        <tbody>
          {resumen.renglones.map(r => (
            <tr key={r.articulo_id} style={st.tr}>
              <td style={{ ...sx.td, ...sx.mono }}>{r.codigo || '—'}</td>
              <td style={sx.td}>{r.descripcion}</td>
              <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{r.total}</td>
              <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{r.despachado}</td>
              <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 600, color: r.pendiente > 0 ? colors.warn : colors.ok }}>{r.pendiente}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {abierto && (
        <ModalDespacharParte
          remito={remito}
          onClose={() => setAbierto(false)}
          onListo={() => { setAbierto(false); onCambio?.() }}
        />
      )}
    </div>
  )
}

function ModalDespacharParte({ remito, onClose, onListo }) {
  const renglones = remito.envio_parcial_resumen.renglones.filter(r => r.pendiente > 0)
  const sugerido = remito.parte_destino_sugerido || {}
  // Cantidades vacías por defecto (regla: campos de cantidad sin 0 precargado).
  const [cant, setCant] = useState({})
  const [destino, setDestino] = useState({
    nombre: sugerido.nombre || '', telefono: sugerido.telefono || '',
    calle: sugerido.calle || '', piso: sugerido.piso || '',
    // La provincia se normaliza al nombre del selector ("CABA" → el de la lista).
    localidad: sugerido.localidad || '', provincia: sugerido.provincia ? matchProvincia(sugerido.provincia) : '',
    codigo_postal: sugerido.codigo_postal || '', notas: '',
  })
  const [fecha, setFecha] = useState('')
  // A domicilio (nota de envío que va a logística) o retiro en sucursal.
  const [retira, setRetira] = useState(false)
  const [sucursal, setSucursal] = useState('')
  const [sucursales, setSucursales] = useState([])
  useEffect(() => {
    api.get('/sucursales-retiro').then(({ data }) => setSucursales(Array.isArray(data) ? data : [])).catch(() => setSucursales([]))
  }, [])
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const esCaba = esCapitalFederal(destino.provincia)
  const set = k => e => setDestino(d => ({ ...d, [k]: e.target.value }))

  const articulos = renglones
    .map(r => ({ articulo_id: r.articulo_id, cantidad: parseInt(cant[r.articulo_id], 10) || 0 }))
    .filter(a => a.cantidad > 0)
  const excedidos = renglones.filter(r => (parseInt(cant[r.articulo_id], 10) || 0) > r.pendiente)
  const valido = articulos.length > 0 && excedidos.length === 0 && destino.nombre.trim() !== '' && (!retira || sucursal !== '')

  const todoLoPendiente = () => setCant(Object.fromEntries(renglones.map(r => [r.articulo_id, String(r.pendiente)])))

  const enviar = async () => {
    setEnviando(true); setError('')
    try {
      await remitosApi.generarNe(remito.id, {
        destinatarios: [retira ? {
          // Retiro: quién lo pasa a buscar y dónde. Sin dirección de entrega.
          nombre: destino.nombre.trim(),
          telefono: destino.telefono || null,
          notas: destino.notas || null,
          retira: true,
          sucursal_retiro: sucursal,
          articulos,
        } : {
          nombre: destino.nombre.trim(),
          telefono: destino.telefono || null,
          calle: destino.calle || null,
          piso: destino.piso || null,
          localidad: esCaba ? null : (destino.localidad || null),
          provincia: destino.provincia || null,
          codigo_postal: destino.codigo_postal || null,
          notas: destino.notas || null,
          fecha_entrega_est: fecha || null,
          articulos,
        }],
      })
      onListo()
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo despachar la parte.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Modal title={`🚚 Despachar parte · remito ${remito.numero_presea}`} onClose={onClose} maxWidth="760px">
      <div style={st.seccion}>
        <div style={st.seccionHead}>
          <span style={st.lbl}>¿Qué sale en esta parte?</span>
          <Boton variante="ghost" sm onClick={todoLoPendiente}>Todo lo pendiente</Boton>
        </div>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Código</th>
              <th style={sx.th}>Descripción</th>
              <th style={{ ...sx.th, textAlign: 'right' }}>Pendiente</th>
              <th style={{ ...sx.th, textAlign: 'right' }}>Sale ahora</th>
            </tr>
          </thead>
          <tbody>
            {renglones.map(r => {
              const pasado = (parseInt(cant[r.articulo_id], 10) || 0) > r.pendiente
              return (
                <tr key={r.articulo_id} style={st.tr}>
                  <td style={{ ...sx.td, ...sx.mono }}>{r.codigo || '—'}</td>
                  <td style={sx.td}>{r.descripcion}</td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{r.pendiente}</td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>
                    <input
                      type="number" min="1" max={r.pendiente} inputMode="numeric"
                      value={cant[r.articulo_id] ?? ''}
                      onChange={e => setCant(c => ({ ...c, [r.articulo_id]: e.target.value }))}
                      style={{ ...st.inpCant, ...(pasado ? { borderColor: colors.errStrong, color: colors.errStrong } : {}) }}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {excedidos.length > 0 && <div style={st.error}>Hay cantidades mayores que lo pendiente.</div>}
      </div>

      <div style={st.seccion}>
        <span style={st.lbl}>¿Cómo sale?</span>
        <div style={st.modos}>
          <label style={st.modo}><input type="radio" checked={!retira} onChange={() => setRetira(false)} /> 🚚 Envío a domicilio</label>
          <label style={st.modo}><input type="radio" checked={retira} onChange={() => setRetira(true)} /> 🏬 Retira en sucursal</label>
        </div>
        {retira ? (
          <p style={st.hint}>No entra a logística: aparece en <strong>Retiros por sucursal</strong>, donde se avisa al cliente que está lista y se marca retirada.</p>
        ) : (
          <p style={st.hint}>Precargada con la dirección de entrega de la venta. Si esta parte va a otro lado, cambiala acá.</p>
        )}
        <div style={st.grid2}>
          {retira && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={st.lblCampo}>¿Dónde lo retira? *</label>
              <select style={{ ...sx.input, width: '100%' }} value={sucursal} onChange={e => setSucursal(e.target.value)}>
                <option value="">— Elegir sucursal —</option>
                {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre} — {s.direccion}</option>)}
              </select>
            </div>
          )}
          <div><label style={st.lblCampo}>{retira ? 'Quién lo retira *' : 'Nombre que recibe *'}</label><input style={sx.input} value={destino.nombre} onChange={set('nombre')} /></div>
          <div><label style={st.lblCampo}>Teléfono</label><input style={sx.input} value={destino.telefono} onChange={set('telefono')} /></div>
          {!retira && (<>
          <div><label style={st.lblCampo}>Domicilio (calle y número)</label><input style={sx.input} value={destino.calle} onChange={set('calle')} /></div>
          <div><label style={st.lblCampo}>Piso / Depto</label><input style={sx.input} value={destino.piso} onChange={set('piso')} placeholder="Ej: 3° B — o timbre" /></div>
          <div>
            <label style={st.lblCampo}>Provincia</label>
            <select style={sx.input} value={destino.provincia} onChange={set('provincia')}>
              <option value="">— Seleccionar —</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {!esCaba && (
            <div>
              <label style={st.lblCampo}>Localidad</label>
              <CampoLocalidad provincia={destino.provincia} value={destino.localidad}
                onChange={v => setDestino(d => ({ ...d, localidad: v }))} inputStyle={sx.input} cp={destino.codigo_postal} />
            </div>
          )}
          <div><label style={st.lblCampo}>Código postal</label><input style={sx.input} value={destino.codigo_postal} onChange={set('codigo_postal')} /></div>
          <div><label style={st.lblCampo}>Fecha pactada (opcional)</label><input type="date" style={sx.input} value={fecha} onChange={e => setFecha(e.target.value)} /></div>
          </>)}
        </div>
        <div style={{ marginTop: space.md }}>
          <label style={st.lblCampo}>Notas para la entrega</label>
          <input style={{ ...sx.input, width: '100%' }} value={destino.notas} onChange={set('notas')} />
        </div>
      </div>

      {error && <div style={st.error}>{error}</div>}
      <div style={st.acciones}>
        <Boton variante="secondary" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" disabled={!valido || enviando} onClick={enviar}>
          {enviando ? 'Despachando…' : `Despachar parte (${articulos.reduce((a, x) => a + x.cantidad, 0)} u.)`}
        </Boton>
      </div>
    </Modal>
  )
}

const st = {
  tr: { height: 40, borderBottom: `1px solid ${colors.borderSoft}` },
  modos: { display: 'flex', gap: space.xl, margin: `${space.sm}px 0` },
  modo: { display: 'flex', alignItems: 'center', gap: space.xs, fontSize: text.body, cursor: 'pointer' },
  header: { display: 'flex', alignItems: 'center', gap: space.lg, marginBottom: space.lg, flexWrap: 'wrap' },
  resumen: { flex: 1, fontSize: text.body, color: colors.textSub },
  hint: { fontSize: text.sec, color: colors.textSec, margin: `${space.xs}px 0 ${space.md}px` },
  seccion: { marginBottom: space.xl },
  seccionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.sm },
  lbl: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  lblCampo: { display: 'block', fontSize: text.sec, color: colors.textSec, marginBottom: 2 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: space.md },
  inpCant: { width: 80, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, textAlign: 'right', fontSize: text.body },
  error: { background: colors.errBg, color: colors.err, padding: `${space.sm}px ${space.md}px`, borderRadius: radius.sm, fontSize: text.sec, marginTop: space.md },
  acciones: { display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.lg },
}
